import { useMemo, useState } from "react";
import { ChevronDown, CheckCircle2, AlertCircle } from "lucide-react";
import type { CyFunRequirement, MaturityEvaluation } from "@/types/maturity";
import { DOMAINS_META } from "@/utils/maturity-colors";
import { useGetScoringAlgorithm } from "@/hooks/api/query/useGetScoringAlgorithm";

const DEFAULT_KEY_MEASURE_TARGET = 3;

type KeyMeasuresTableProps = {
  evaluation: MaturityEvaluation;
  requirements: CyFunRequirement[];
};

type KeyMeasureRow = {
  code: string;
  name: string;
  domainId: string;
  score: number | null;
  passed: boolean;
};

function getDomainId(code: string): string {
  return code.split(".")[0] ?? "";
}

function buildRows(
  keyMeasureCodes: string[],
  requirements: CyFunRequirement[],
  evaluation: MaturityEvaluation,
  keyMeasureTarget: number
): KeyMeasureRow[] {
  const reqByCode = new Map(requirements.map((r) => [r.code, r]));
  const rows: KeyMeasureRow[] = [];
  for (const code of keyMeasureCodes) {
    const req = reqByCode.get(code);
    const d = evaluation.full_responses[`${code}-D`];
    const i = evaluation.full_responses[`${code}-I`];
    const hasBoth = d !== undefined && i !== undefined;
    const score = hasBoth ? (Number(d) + Number(i)) / 2 : null;
    rows.push({
      code,
      name: req?.name ?? code,
      domainId: getDomainId(code),
      score,
      passed: score !== null && score >= keyMeasureTarget,
    });
  }
  return rows;
}

function KeyMeasuresTable({ evaluation, requirements }: KeyMeasuresTableProps) {
  const { data: scoringAlgorithm } = useGetScoringAlgorithm();

  const levelConfig = useMemo(() => {
    if (!scoringAlgorithm) return null;
    return (
      scoringAlgorithm.assurance_levels.find(
        (l) =>
          l.name === evaluation.assurance_level ||
          l.code === evaluation.assurance_level
      ) ?? null
    );
  }, [scoringAlgorithm, evaluation.assurance_level]);

  const keyMeasureCodes = useMemo(
    () => levelConfig?.key_measures ?? [],
    [levelConfig]
  );

  const keyMeasureTarget =
    levelConfig?.key_measure_minimum ?? DEFAULT_KEY_MEASURE_TARGET;

  const rows = useMemo(
    () =>
      buildRows(keyMeasureCodes, requirements, evaluation, keyMeasureTarget),
    [keyMeasureCodes, requirements, evaluation, keyMeasureTarget]
  );

  const rowsByDomain = useMemo(() => {
    const map: Record<string, KeyMeasureRow[]> = {};
    for (const row of rows) {
      if (!map[row.domainId]) map[row.domainId] = [];
      map[row.domainId].push(row);
    }
    return map;
  }, [rows]);

  const initialExpanded = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const domain of DOMAINS_META) {
      const domainRows = rowsByDomain[domain.id] ?? [];
      out[domain.id] = domainRows.some((r) => !r.passed);
    }
    return out;
  }, [rowsByDomain]);

  const [expandedDomains, setExpandedDomains] =
    useState<Record<string, boolean>>(initialExpanded);

  const toggleDomain = (id: string) => {
    setExpandedDomains((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totalRows = rows.length;
  const scoredRows = rows.filter((r) => r.score !== null);
  const totalAvg =
    scoredRows.length > 0
      ? scoredRows.reduce((sum, r) => sum + (r.score ?? 0), 0) /
        scoredRows.length
      : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[480px]">
          <div className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-2.5 bg-slate-900 text-xs font-semibold text-white uppercase tracking-wide">
            <span>Măsură-cheie</span>
            <span className="text-center">Țintă</span>
            <span className="text-center">Scor</span>
            <span className="text-center">Stare</span>
          </div>

          {DOMAINS_META.map((domain) => {
            const domainRows = rowsByDomain[domain.id] ?? [];
            if (domainRows.length === 0) return null;
            const isExpanded = expandedDomains[domain.id];
            const passedCount = domainRows.filter((r) => r.passed).length;
            const failedCount = domainRows.length - passedCount;
            const allPassed = failedCount === 0;
            const domainScored = domainRows.filter((r) => r.score !== null);
            const domainAvg =
              domainScored.length > 0
                ? domainScored.reduce((sum, r) => sum + (r.score ?? 0), 0) /
                  domainScored.length
                : null;

            return (
              <div key={domain.id}>
                <button
                  onClick={() => toggleDomain(domain.id)}
                  className="w-full grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-3 hover:bg-slate-50 transition-colors items-center text-left"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-1.5 h-8 rounded-full"
                      style={{ backgroundColor: domain.color }}
                    />
                    <div>
                      <div
                        className="font-semibold text-sm"
                        style={{ color: domain.color }}
                      >
                        {domain.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {domainRows.length} măsuri-cheie
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </div>
                  <div className="text-center text-sm text-slate-600">
                    {keyMeasureTarget.toFixed(2)}
                  </div>
                  <div className="text-center font-semibold text-sm text-slate-700">
                    {domainAvg !== null ? domainAvg.toFixed(2) : "—"}
                  </div>
                  <div className="flex items-center justify-center">
                    {allPassed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        Conform
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" />
                        {failedCount} sub țintă
                      </span>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {domainRows.map((row) => {
                      const scoreLabel =
                        row.score !== null ? row.score.toFixed(2) : "—";
                      const progress =
                        row.score !== null
                          ? Math.min((row.score / keyMeasureTarget) * 100, 100)
                          : 0;
                      return (
                        <div
                          key={row.code}
                          className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-2 pl-12 text-sm text-slate-600 items-center"
                        >
                          <div className="min-w-0">
                            <span className="font-mono text-xs text-slate-400 mr-2">
                              {row.code}
                            </span>
                            <span className="text-xs">
                              {row.name.split(":")[0]}
                            </span>
                          </div>
                          <div className="text-center text-slate-500">
                            {keyMeasureTarget.toFixed(2)}
                          </div>
                          <div
                            className={`text-center font-medium ${
                              row.score === null
                                ? "text-slate-400"
                                : row.passed
                                  ? "text-emerald-700"
                                  : "text-amber-700"
                            }`}
                          >
                            {scoreLabel}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${progress}%`,
                                  background: row.passed
                                    ? `linear-gradient(90deg, ${domain.color}, ${domain.pill})`
                                    : "linear-gradient(90deg, #f59e0b, #fbbf24)",
                                  opacity: row.score === null ? 0 : 0.85,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-3 bg-slate-900 text-white border-t border-slate-200 font-bold text-sm items-center">
            <span>TOTAL</span>
            <span className="text-center">{keyMeasureTarget.toFixed(2)}</span>
            <span className="text-center">
              {totalAvg !== null ? totalAvg.toFixed(2) : "—"}
            </span>
            <span className="text-center text-xs font-normal opacity-80">
              {totalRows > 0
                ? `${rows.filter((r) => r.passed).length}/${totalRows} conforme`
                : ""}
            </span>
          </div>

          <div className="px-4 py-2 text-xs text-slate-400 italic border-t">
            Prag DNSC: fiecare măsură-cheie ≥ {keyMeasureTarget}/5
          </div>
        </div>
      </div>
    </div>
  );
}

export default KeyMeasuresTable;
